import React from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconArrowUpCircle, IconArrowDownCircle } from '@tabler/icons-react';
import Localization from '../Localization.jsx';

/** Default sort direction for admin / shared list UIs. */
export const DEFAULT_SORT_ORDER = 'DESC';

/**
 * Icon toggle for ascending / descending sort order.
 *
 * Accepts either uppercase ("ASC"/"DESC") or lowercase ("asc"/"desc") values
 * and returns the toggled value in the same casing style.
 * Missing / invalid values are treated as DESC.
 *
 * Usage:
 *   <SortOrderToggle value={order} onChange={setOrder} />
 */
export function SortOrderToggle({
  value = DEFAULT_SORT_ORDER,
  onChange,
  variant = 'light',
  size = 'lg',
  color,
  iconSize = 18,
  disabled = false,
}) {
  const normalized = String(value || DEFAULT_SORT_ORDER).toUpperCase();
  const isAsc = normalized === 'ASC';
  const preserveLower = typeof value === 'string' && value.length > 0 && value === value.toLowerCase();
  const ascLabel = Localization.strings.ASCENDING || '升序';
  const descLabel = Localization.strings.DESCENDING || '降序';

  const handleClick = () => {
    if (disabled || typeof onChange !== 'function') return;
    const nextUpper = isAsc ? 'DESC' : 'ASC';
    onChange(preserveLower ? nextUpper.toLowerCase() : nextUpper);
  };

  return (
    <Tooltip label={isAsc ? ascLabel : descLabel}>
      <ActionIcon
        variant={variant}
        size={size}
        color={color}
        onClick={handleClick}
        disabled={disabled}
        aria-label={isAsc ? ascLabel : descLabel}
      >
        {isAsc
          ? <IconArrowUpCircle size={iconSize} />
          : <IconArrowDownCircle size={iconSize} />}
      </ActionIcon>
    </Tooltip>
  );
}

export default SortOrderToggle;
